import type {
    CommerceContext,
    CommerceErrorCode,
    CommerceOperationMiddleware,
    CommerceOperationOptions,
    CommerceSession,
} from "@teqtank/commerce-contracts";
import { CommerceTransportError } from "@teqtank/commerce-transport";
import type {
    CommerceOperation,
    CommerceTransport,
    CommerceTransportContext,
} from "@teqtank/commerce-transport";
import { CommerceError } from "../errors/commerce-error.js";
import { createCorrelationId } from "../utilities/create-correlation-id.js";

export class CommerceOperationExecutor {
    public constructor(
        private readonly transport: CommerceTransport,
        private readonly session: CommerceSession,
        private readonly getContext: () => CommerceContext,
        private readonly assertRuntimeAvailable: () => void,
        private readonly middleware: readonly CommerceOperationMiddleware[] = [],
    ) {}

    public async execute<TRequest, TResponse>(
        operation: CommerceOperation<TRequest, TResponse>,
        request: TRequest,
        options: CommerceOperationOptions,
        errorCode: CommerceErrorCode,
        errorMessage: string,
        details: Readonly<Record<string, unknown>> = {},
    ): Promise<TResponse> {
        this.assertRuntimeAvailable();

        try {
            const context = this.createContext(options);
            const invokeTransport = () => this.transport.execute(operation, request, context);
            const pipeline = this.middleware.reduceRight<() => Promise<unknown>>(
                (next, middleware) => () =>
                    middleware(
                        {
                            operation: operation as unknown as CommerceOperation<unknown, unknown>,
                            request,
                            context,
                        },
                        next,
                    ),
                invokeTransport,
            );
            return (await pipeline()) as TResponse;
        } catch (cause) {
            if (cause instanceof CommerceError) {
                throw cause;
            }

            const context = this.getContext();
            const resolvedMessage =
                cause instanceof CommerceTransportError && cause.message.trim().length > 0
                    ? cause.message
                    : errorMessage;
            throw new CommerceError(errorCode, resolvedMessage, {
                operationName: operation.name,
                storefront: context.storefront.alias,
                marketId: context.market.id,
                ...details,
                cause,
            });
        }
    }

    private createContext(options: CommerceOperationOptions): CommerceTransportContext {
        const context = this.getContext();
        return {
            correlationId: createCorrelationId(),
            sessionToken: this.session.token,
            commerceKey: this.session.commerceKey,
            dataSource: this.session.dataSource,
            tenantId: context.tenant.id,
            marketId: context.market.id,
            currencyCode: context.currency.code,
            locale: context.locale.code,
            ...(options.signal !== undefined ? { signal: options.signal } : {}),
        };
    }
}
